import Mark from "mark.js/src/lib/mark";
import type { Spotlight } from "../types";
import { getScrollRoot } from "./getScrollRoot";

const SPOTLIGHT_STYLES_ID = "viewer-express-spotlight-styles";

/** Same exclude list as plugins-lib Spotlight for consistent behavior */
const MARK_EXCLUDE = [
  ".key-term *",
  ".foot-note *",
  ".popover-wrapper *",
  ".popupTip *",
  // Footnote marker text (e.g. <sup>158</sup>) is not part of the TTS offsets; key-term text is.
  ".spf-inline-footnote *",
  "span[data-slate-zero-width='z']",
  ".codeEditorContainer *",
  ".gutterContainer *",
  ".video-error *",
];

/** Short/common words must use TTS character offsets — text search matches the wrong occurrence. */
const MIN_TEXT_FALLBACK_LENGTH = 4;

type SpotlightMarkItem = Spotlight["mark"][number];

interface SelectorSpotlightState {
  element: HTMLElement;
  markInstance: Mark;
  lastHighlightedWord: SpotlightMarkItem | null;
  lastHighlightedSentence: SpotlightMarkItem | null;
  generation: number;
}

const spotlightBySelector = new Map<string, SelectorSpotlightState>();

function getOrCreateSpotlightStyles(): HTMLStyleElement {
  let el = document.getElementById(SPOTLIGHT_STYLES_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = SPOTLIGHT_STYLES_ID;
    document.head.appendChild(el);
  }
  return el;
}

function sanitizeColor(color: string): string {
  return color.replace(/[^a-zA-Z0-9]/g, "");
}

/** A batch apply scrolls once per entry; first request wins so focus lands on the first highlight. */
let hasScrollClaim = false;

const SCROLL_MARGIN_PX = 16;

function isActivelyScrollable(el: HTMLElement): boolean {
  return el.scrollHeight > el.clientHeight + 1;
}

/**
 * Scroll TTS highlight into view for both:
 * - Web nested scroll`.content-frame` / getScrollRoot when it overflows
 * - Native WebView/document scroll: window.scrollTo
 */
function scrollNodeIntoReadingView(node: HTMLElement, scrollRoot: HTMLElement | null): void {
  if (scrollRoot && isActivelyScrollable(scrollRoot)) {
    const rootRect = scrollRoot.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    scrollRoot.scrollTo({
      top: scrollRoot.scrollTop + (nodeRect.top - rootRect.top) - SCROLL_MARGIN_PX,
      behavior: "smooth",
    });
    return;
  }

  window.scrollTo({
    top: node.getBoundingClientRect().top + window.scrollY - SCROLL_MARGIN_PX,
    behavior: "auto",
  });
}

function scrollMarkToView(node: HTMLElement, container: HTMLElement): void {
  if (hasScrollClaim) return;
  hasScrollClaim = true;
  Promise.resolve().then(() => {
    hasScrollClaim = false;
  });

  const scrollRoot = getScrollRoot(container);
  // Viewport root: native document/WebView scroll is detected; nested overflow
  // clipping on web is still respected by IntersectionObserver.
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0].isIntersecting) {
        scrollNodeIntoReadingView(node, scrollRoot);
      }
      observer.disconnect();
    },
    { root: null, rootMargin: "0px", threshold: 1.0 },
  );
  observer.observe(node);
}

function isEmptyNode(node: HTMLElement): boolean {
  return node.textContent?.trim() === "" || node.innerHTML.trim() === "";
}

function isSameSpotlightMark(a: SpotlightMarkItem | null, b: SpotlightMarkItem | null): boolean {
  return !!(
    a?.startOffset === b?.startOffset &&
    a?.length === b?.length &&
    a?.backgroundColor === b?.backgroundColor &&
    a?.text === b?.text
  );
}

function sortMarksByLengthDesc(
  a: SpotlightMarkItem,
  b: SpotlightMarkItem,
): [SpotlightMarkItem, SpotlightMarkItem] {
  return (a.length >= b.length ? [a, b] : [b, a]) as [SpotlightMarkItem, SpotlightMarkItem];
}

function resolveSpotlightElement(container: HTMLElement, selector: string): HTMLElement | null {
  const inContainer = container.querySelector(`[data-id="${selector}"]`);
  if (inContainer instanceof HTMLElement) {
    return inContainer;
  }

  const global = document.querySelector(`[data-id="${selector}"]`);
  return global instanceof HTMLElement ? global : null;
}

function getSelectorState(container: HTMLElement, selector: string): SelectorSpotlightState | null {
  const element = resolveSpotlightElement(container, selector);
  if (!element) {
    return null;
  }

  const existing = spotlightBySelector.get(selector);
  if (existing) {
    if (existing.element !== element) {
      existing.element = element;
      existing.markInstance = new Mark(element);
      existing.lastHighlightedWord = null;
      existing.lastHighlightedSentence = null;
    }
    return existing;
  }

  const state: SelectorSpotlightState = {
    element,
    markInstance: new Mark(element),
    lastHighlightedWord: null,
    lastHighlightedSentence: null,
    generation: 0,
  };
  spotlightBySelector.set(selector, state);
  return state;
}

/** Keeps every selector of the current apply; drops highlights left over from previous ones. */
function clearSelectorsExcept(activeSelectors: ReadonlySet<string>): void {
  for (const selector of [...spotlightBySelector.keys()]) {
    if (!activeSelectors.has(selector)) {
      clearSelectorState(selector);
    }
  }
}

function clearSelectorState(selector: string): void {
  const state = spotlightBySelector.get(selector);
  if (!state) {
    return;
  }
  state.markInstance.unmark();
  spotlightBySelector.delete(selector);
}

function getSpotlightMarks(spotlight: Spotlight): SpotlightMarkItem[] {
  return Array.isArray(spotlight.mark) ? spotlight.mark : [];
}

function resolveSpotlightSearchText(spotlight: Spotlight, marks: SpotlightMarkItem[]): string {
  const topLevel = spotlight.text?.trim();
  if (topLevel) {
    return topLevel;
  }
  for (const mark of marks) {
    const markText = mark.text?.trim();
    if (markText) {
      return markText;
    }
  }
  return "";
}

function buildTextSearchMark(marks: SpotlightMarkItem[], searchText: string): SpotlightMarkItem {
  const template = marks[0];
  return {
    startOffset: 0,
    length: 0,
    backgroundColor: template?.backgroundColor ?? "#DAD8FF",
    isUnderLineText: template?.isUnderLineText,
    underLineColor: template?.underLineColor,
    text: searchText,
  };
}

function highlightEntireElement(
  markInstance: Mark,
  element: HTMLElement,
  markOptions: Record<string, unknown>,
): void {
  const range = document.createRange();
  range.selectNodeContents(element);

  const textLength = range.toString().length;
  if (textLength === 0) return;

  markInstance.markRanges(
    [
      {
        start: 0,
        length: textLength,
      },
    ],
    markOptions,
  );
}

function getHighlightClassName(item: SpotlightMarkItem): string {
  const bgColor = item.backgroundColor ?? "transparent";
  const base = `viewer-express-highlight-${sanitizeColor(bgColor)}`;
  if (!item.isUnderLineText) {
    return base;
  }
  const underlineSuffix = item.underLineColor ? `-ul-${sanitizeColor(item.underLineColor)}` : "-ul";
  return `${base}${underlineSuffix}`;
}

function ensureHighlightStyle(
  styleElement: HTMLStyleElement,
  highlightClass: string,
  item: SpotlightMarkItem,
): void {
  if (styleElement.innerText.includes(`.${highlightClass}{`)) {
    return;
  }

  const bgColor = item.backgroundColor ?? "transparent";
  styleElement.innerText += `.${highlightClass}{ background-color: ${bgColor}; ${
    item.isUnderLineText ? "text-decoration: underline;" : ""
  }; ${item.underLineColor ? `text-decoration-color: ${item.underLineColor};` : ""} }\n`;
}

function elementHasMark(element: HTMLElement, highlightClass: string): boolean {
  return element.querySelector(`mark.${highlightClass}`) != null;
}

function shouldUseTtsTextFallback(text: string | undefined): boolean {
  return (text?.trim().length ?? 0) >= MIN_TEXT_FALLBACK_LENGTH;
}

function markItem(
  container: HTMLElement,
  element: HTMLElement,
  markInstance: Mark,
  item: SpotlightMarkItem,
  isWord: boolean,
  useTextSearch = false,
  searchText?: string,
): void {
  if (!item) return;

  const highlightClass = getHighlightClassName(item);

  const styleElement = getOrCreateSpotlightStyles();
  ensureHighlightStyle(styleElement, highlightClass, item);

  const markOptions = {
    element: "mark",
    className: highlightClass,
    each(node: Node) {
      if (node instanceof HTMLElement) {
        if (item.isUnderLineText) {
          node.style.textDecoration = "underline";
          if (item.underLineColor) {
            node.style.textDecorationColor = item.underLineColor;
          }
        }
        if (isWord && !isEmptyNode(node)) {
          scrollMarkToView(node, container);
        }
      }
    },
    exclude: MARK_EXCLUDE,
    separateWordSearch: !useTextSearch,
    acrossElements: useTextSearch,
  };

  const textToFind = (searchText ?? item.text)?.trim() ?? "";

  if (useTextSearch) {
    if (textToFind !== "") {
      markInstance.mark(textToFind, markOptions);
    } else {
      highlightEntireElement(markInstance, element, markOptions);
    }
    return;
  }

  if (item.length > 0) {
    markInstance.markRanges([{ start: item.startOffset, length: item.length }], {
      ...markOptions,
      acrossElements: false,
      separateWordSearch: true,
    });
    if (elementHasMark(element, highlightClass)) {
      return;
    }

    markInstance.markRanges([{ start: item.startOffset, length: item.length }], {
      ...markOptions,
      acrossElements: true,
      separateWordSearch: true,
    });
    if (elementHasMark(element, highlightClass)) {
      return;
    }
  }

  // TTS offsets can drift from mark.js countable text (math, key terms, etc.).
  if (textToFind && shouldUseTtsTextFallback(textToFind)) {
    markInstance.mark(textToFind, {
      ...markOptions,
      acrossElements: true,
      separateWordSearch: false,
    });
  }
}

export function applySpotlightToElement(
  container: HTMLElement,
  spotlight: Spotlight,
  activeSelectors?: ReadonlySet<string>,
): void {
  const { selector } = spotlight;
  const selectorState = getSelectorState(container, selector);
  if (!selectorState) return;

  clearSelectorsExcept(activeSelectors ?? new Set([selector]));

  const marks = getSpotlightMarks(spotlight);
  const useTextSearch = Boolean(spotlight.useTextSearch);
  const searchText = useTextSearch ? resolveSpotlightSearchText(spotlight, marks) : "";

  if (!useTextSearch && !marks.length) {
    return;
  }

  const { markInstance, lastHighlightedWord, lastHighlightedSentence } = selectorState;

  let sentenceMark: SpotlightMarkItem | null = null;
  let wordMark: SpotlightMarkItem | null = null;

  if (marks.length === 2) {
    [sentenceMark, wordMark] = sortMarksByLengthDesc(marks[0], marks[1]);
  } else if (marks.length === 1) {
    sentenceMark = marks[0];
  } else if (useTextSearch) {
    sentenceMark = buildTextSearchMark(marks, searchText);
  } else {
    return;
  }

  if (
    wordMark &&
    isSameSpotlightMark(lastHighlightedWord, wordMark) &&
    isSameSpotlightMark(lastHighlightedSentence, sentenceMark)
  ) {
    return;
  }

  const generation = ++selectorState.generation;
  const runIfCurrent = (fn: () => void): void => {
    if (
      spotlightBySelector.get(selector) === selectorState &&
      selectorState.generation === generation
    ) {
      fn();
    }
  };

  const topLevelSearchText = searchText;
  const prevWord = lastHighlightedWord;
  const prevSentence = lastHighlightedSentence;

  selectorState.lastHighlightedSentence = sentenceMark;
  selectorState.lastHighlightedWord = wordMark;

  if (!wordMark) {
    markInstance.unmark({
      done: () => {
        runIfCurrent(() => {
          markItem(
            container,
            selectorState.element,
            markInstance,
            sentenceMark!,
            true,
            useTextSearch,
            topLevelSearchText,
          );
        });
      },
    });
    return;
  }

  if (prevWord && isSameSpotlightMark(prevSentence, sentenceMark)) {
    markInstance.unmark({
      className: getHighlightClassName(prevWord),
      done: () => {
        runIfCurrent(() => {
          if (!wordMark) return;
          markItem(
            container,
            selectorState.element,
            markInstance,
            wordMark,
            true,
            useTextSearch,
            topLevelSearchText,
          );
        });
      },
    });
    return;
  }

  markInstance.unmark({
    done: () => {
      runIfCurrent(() => {
        if (sentenceMark) {
          markItem(
            container,
            selectorState.element,
            markInstance,
            sentenceMark,
            false,
            useTextSearch,
            topLevelSearchText,
          );
        }
        if (wordMark) {
          markItem(
            container,
            selectorState.element,
            markInstance,
            wordMark,
            true,
            useTextSearch,
            topLevelSearchText,
          );
        }
      });
    },
  });
}

export function clearSpotlightFromElement(_container: HTMLElement, selector: string): void {
  clearSelectorState(selector);
}

export function applySpotlight(containerId: string, spotlight: Spotlight): void {
  const container = document.getElementById(containerId);
  if (!container) return;
  if (!isValidSpotlightConfig(spotlight)) return;
  applySpotlightToElement(container, spotlight);
}

export function applySpotlights(containerId: string, spotlights: Spotlight[]): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  const items = spotlights.filter(isValidSpotlightConfig);
  if (!items.length) return;

  // Every entry of the batch stays highlighted, so the batch clears stale selectors only once.
  const activeSelectors = new Set(items.map((item) => item.selector));
  clearSelectorsExcept(activeSelectors);

  for (const spotlight of items) {
    applySpotlightToElement(container, spotlight, activeSelectors);
  }
}

export function clearSpotlight(containerId: string, selector: string): void {
  const container = document.getElementById(containerId);
  if (!container) return;
  clearSpotlightFromElement(container, selector);
}

export function clearSpotlights(containerId: string, selectors: Iterable<string>): void {
  for (const selector of selectors) {
    clearSpotlight(containerId, selector);
  }
}

export function clearAllSpotlights(): void {
  for (const selector of [...spotlightBySelector.keys()]) {
    clearSelectorState(selector);
  }
}

export function isValidSpotlightConfig(spotlight: Spotlight): boolean {
  if (!spotlight?.selector || /['"]/.test(spotlight.selector)) {
    return false;
  }
  if (spotlight.mark?.length) {
    return true;
  }
  return Boolean(spotlight.useTextSearch);
}
